/**
 * Componente unificado para gerenciar Time Conditions
 * VigosCloud - Time Condition System
 * 
 * Componente compartilhado para todos os módulos (InternalCalls, IncomingCalls, OutboundCalls, URA, Message)
 * Baseado no padrão URA com dropdown de contextos
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faPlus, 
  faTrash,
  faInfoCircle,
  faClock,
  faArrowUp,
  faArrowDown
} from '@fortawesome/free-solid-svg-icons';

import './TimeConditionManager.css';

function TimeConditionManager({ 
  rules = [],
  onChange,
  availableContexts = [],
  disabled = false,
  title = 'Desvios por Data/Hora'
}) {

  const addRule = () => {
    const newRules = [...rules, {
      sequence_order: rules.length + 1,
      time_range: '08:00-18:00',
      days_of_week: 'mon-fri',
      days_of_month: '*',
      months: '*',
      destination: '',
      destination_type: 'context',
      context_name: '',
      extension_name: 'msg',
      priority: 1,
      description: '',
      active: true
    }];
    if (onChange) {
      onChange(newRules);
    }
  };

  const removeRule = (index) => {
    const newRules = rules.filter((_, i) => i !== index);
    // Reordenar sequence_order
    newRules.forEach((rule, i) => {
      rule.sequence_order = i + 1;
    });
    if (onChange) {
      onChange(newRules);
    }
  };

  const updateRule = (index, field, value) => {
    const newRules = [...rules];
    newRules[index] = {
      ...newRules[index],
      [field]: value
    };
    if (onChange) {
      onChange(newRules);
    }
  };

  const moveRuleUp = (index) => {
    if (index === 0) return;
    const newRules = [...rules];
    [newRules[index - 1], newRules[index]] = [newRules[index], newRules[index - 1]];
    newRules.forEach((rule, i) => {
      rule.sequence_order = i + 1;
    });
    if (onChange) {
      onChange(newRules);
    }
  };

  const moveRuleDown = (index) => {
    if (index === rules.length - 1) return;
    const newRules = [...rules];
    [newRules[index], newRules[index + 1]] = [newRules[index + 1], newRules[index]];
    newRules.forEach((rule, i) => {
      rule.sequence_order = i + 1;
    });
    if (onChange) {
      onChange(newRules);
    }
  };

  return (
    <div className="time-condition-card">
      <div className="time-condition-header">
        <h4 className="time-condition-title">
          <FontAwesomeIcon icon={faCalendarAlt} />
          {title}
        </h4>
        <button
          type="button"
          className="btn-add-rule"
          onClick={addRule}
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faPlus} />
          Adicionar Regra
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="time-condition-empty">
          <strong>Nenhuma condição de tempo configurada</strong>
          <p>Clique em "Adicionar Regra" para desviar baseado em horário.</p>
        </div>
      ) : (
        <>
          <div className="time-condition-examples">
            <h5 className="subtitle">
              <FontAwesomeIcon icon={faInfoCircle} />
              Exemplos de Configuração
            </h5>
            <ul>
              <li><strong>Comercial (Seg-Sex 8h-18h):</strong> <code>08:00-17:59 | mon-fri | * | *</code></li>
              <li><strong>Fim de semana:</strong> <code>* | sat-sun | * | *</code></li>
              <li><strong>Feriados (1º e 25):</strong> <code>* | * | 1,25 | *</code></li>
              <li><strong>Janeiro a Junho:</strong> <code>* | * | * | jan-jun</code></li>
            </ul>
          </div>

          {rules.map((rule, index) => (
            <div key={index} className="time-rule-card">
              <div className="time-rule-header">
                <h5 className="time-rule-title">
                  <FontAwesomeIcon icon={faClock} />
                  Regra #{index + 1}
                </h5>
                <div className="time-rule-actions">
                  <button
                    type="button"
                    className="btn-move-rule btn-move-up"
                    onClick={() => moveRuleUp(index)}
                    disabled={disabled || index === 0}
                    title="Mover para cima"
                  >
                    <FontAwesomeIcon icon={faArrowUp} />
                  </button>
                  <button
                    type="button"
                    className="btn-move-rule btn-move-down"
                    onClick={() => moveRuleDown(index)}
                    disabled={disabled || index === rules.length - 1}
                    title="Mover para baixo"
                  >
                    <FontAwesomeIcon icon={faArrowDown} />
                  </button>
                  <button
                    type="button"
                    className="btn-remove-rule"
                    onClick={() => removeRule(index)}
                    disabled={disabled}
                    title="Remover regra"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>

              <div className="time-rule-fields">
                <div className="time-rule-field field-small">
                  <label className="time-rule-label">Horário *</label>
                  <input
                    className="time-rule-input"
                    type="text"
                    placeholder="08:00-18:00 ou *"
                    value={rule.time_range || ''}
                    onChange={(e) => updateRule(index, 'time_range', e.target.value)}
                    disabled={disabled}
                  />
                  <p className="time-rule-help">HH:MM-HH:MM ou *</p>
                </div>

                <div className="time-rule-field field-small">
                  <label className="time-rule-label">Dias Semana *</label>
                  <input
                    className="time-rule-input"
                    type="text"
                    placeholder="mon-fri ou *"
                    value={rule.days_of_week || ''}
                    onChange={(e) => updateRule(index, 'days_of_week', e.target.value)}
                    disabled={disabled}
                  />
                  <p className="time-rule-help">mon-fri, sat-sun, *</p>
                </div>

                <div className="time-rule-field field-small">
                  <label className="time-rule-label">Dias Mês *</label>
                  <input
                    className="time-rule-input"
                    type="text"
                    placeholder="* ou 1-15"
                    value={rule.days_of_month || ''}
                    onChange={(e) => updateRule(index, 'days_of_month', e.target.value)}
                    disabled={disabled}
                  />
                  <p className="time-rule-help">*, 1-15, 1,15</p>
                </div>

                <div className="time-rule-field field-small">
                  <label className="time-rule-label">Meses *</label>
                  <input
                    className="time-rule-input"
                    type="text"
                    placeholder="* ou jan-jun"
                    value={rule.months || ''}
                    onChange={(e) => updateRule(index, 'months', e.target.value)}
                    disabled={disabled}
                  />
                  <p className="time-rule-help">*, jan-dec, jan,jul</p>
                </div>
              </div>

              <div className="time-rule-fields">
                <div className="time-rule-field field-large">
                  <label className="time-rule-label">Contexto de Destino *</label>
                  <select
                    className="time-rule-select"
                    value={rule.context_name || ''}
                    onChange={(e) => updateRule(index, 'context_name', e.target.value)}
                    disabled={disabled}
                  >
                    <option value="">Selecione um contexto</option>
                    {availableContexts.map(context => (
                      <option key={context.name} value={context.name}>
                        {context.label || context.name}
                      </option>
                    ))}
                  </select>
                  <p className="time-rule-help">Contexto de destino</p>
                </div>

                <div className="time-rule-field field-medium">
                  <label className="time-rule-label">Extensão *</label>
                  <input
                    className="time-rule-input"
                    type="text"
                    placeholder="msg ou s"
                    value={rule.extension_name || 'msg'}
                    onChange={(e) => updateRule(index, 'extension_name', e.target.value)}
                    disabled={disabled}
                  />
                  <p className="time-rule-help">Extensão do contexto</p>
                </div>

                <div className="time-rule-field field-small">
                  <label className="time-rule-label">Prioridade</label>
                  <input
                    className="time-rule-input"
                    type="number"
                    min="1"
                    placeholder="1"
                    value={rule.priority || 1}
                    onChange={(e) => updateRule(index, 'priority', parseInt(e.target.value) || 1)}
                    disabled={disabled}
                  />
                  <p className="time-rule-help">Padrão: 1</p>
                </div>
              </div>

              <div className="time-rule-fields">
                <div className="time-rule-field">
                  <label className="time-rule-label">Descrição</label>
                  <input
                    className="time-rule-input"
                    type="text"
                    placeholder="Ex: Fora do horário"
                    value={rule.description || ''}
                    onChange={(e) => updateRule(index, 'description', e.target.value)}
                    disabled={disabled}
                  />
                  <p className="time-rule-help">Descrição opcional</p>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default TimeConditionManager;


